import { useState, useEffect } from "react";
import config from "../../config";
import fetchWithAuth from "../../services/fetchWithAuth";
import { getCurrentlyLoggedInUser } from "../../CommonFunctions";
import PopupModel from "../Popup/PopupModel";
import { usePopup } from "../Popup/PopupContext";
import { ParticipantBuyout } from "../../Interfaces/Buyout";
import FundraiserBuyoutDownload from "./BuyoutPrintout";
interface FileGenerationButtonsProps {
  fundraiserID: number;
  fundraiserName: string;
}

const FileGenerationButtons: React.FC<FileGenerationButtonsProps> = ({
  fundraiserID,
  fundraiserName,
}) => {
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);
  const [currentGenerated, setCurrentGenerated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // New state for loading
  const BASE_API = config.API_BASE_URL;
  const { showPopup, hidePopup } = usePopup();
  const [buyoutParticipants, setBuyoutParticipants] = useState<
    ParticipantBuyout[]
  >([]);

  const handleFirstButtonClick = async () => {
    const data = {
      fundraiser: fundraiserID,
      account: (await getCurrentlyLoggedInUser()).id,
    };
    setLoading(true); // Set loading to true when the request starts
    try {
      const response = await fetchWithAuth(
        `${BASE_API}fundraiser/file-creation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (response.status === 409 || response.status === 501) {
        showPopup(
          fileGenerationWarningPopup.header,
          fileGenerationWarningPopup.body,
          fileGenerationWarningPopup.buttons,
          fileGenerationWarningPopup.width
        );

        return;
      }
      if (response.status === 429) {
        showPopup(
          fileGenerationFailurePopup.header,
          fileGenerationFailurePopup.body,
          fileGenerationFailurePopup.buttons,
          fileGenerationFailurePopup.width
        );

        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      document.body.appendChild(link);
      link.style.display = "none"; // Hide the link element

      link.href = url;
      link.setAttribute("download", fundraiserName);
      link.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      // Update UI to reflect that files were generated
      const currentTime = new Date();
      setCurrentGenerated(currentTime.toLocaleString());
    } catch (error) {
      console.error("Error generating/downloading PDF:", error);
    } finally {
      setLoading(false);
    }
  };

  const markFundraiserAsComplete = async () => {
    if (fundraiserID) {
      setLoading(true);
      try {
        const response = await fetchWithAuth(
          `${config.API_BASE_URL}fundraisers/${fundraiserID}/complete/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status == 409) {
          showPopup(
            shipToHomeOrderErrorPopUp.header,
            shipToHomeOrderErrorPopUp.body,
            shipToHomeOrderErrorPopUp.buttons,
            shipToHomeOrderErrorPopUp.width
          );
        } else if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        console.log(response);
        // If it was successful reolaod
        if (response.status == 201) {
          window.location.reload();
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // check to see if the files already exist on load up
    // and when they were generated
    // no api endpoint exists yet
    const fetchFilesIfTheyExist = async () => {
      try {
        const response = await fetchWithAuth(
          `${BASE_API}fundraiser/file-generation/${fundraiserID}`
        );
        if (response.status === 200) {
          const data = await response.json();
          setLastGenerated(data.timestamp);
        }
      } catch (error) {
        console.error("Error with initial API request:", error);
      }
    };
    const fetchBuyoutParticipants = async () => {
      try {
        const response = await fetchWithAuth(
          `${config.API_BASE_URL}buyout/${fundraiserID}/`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch participants");
        }
        const data = await response.json();
        setBuyoutParticipants(data);
      } catch (error) {
        console.error("Error fetching participants:", error);
      } finally {
        setLoading(false); // Ensure loading state is updated
      }
    };

    fetchFilesIfTheyExist();
    fetchBuyoutParticipants();
  }, []);

  const fileGenerationWarningPopup = new PopupModel(
    "Fundraiser File Warning!", // header
    (
      <div>
        The following fundraiser has recently been processed. Due to use of
        significant server resources, this action can only be done once every 24
        hours. If there is immediate need for this file download please contact
        admin with the fundraiser name and org for this restriction to be
        overrided
      </div> //body
    ),
    [
      {
        label: "Okay",
        onClick: () => {
          hidePopup();
        },
      },
    ],
    "40vw" //width
  );

  const fileGenerationFailurePopup = new PopupModel(
    "Fundraiser Too Many Failures Warning!", // header
    (
      <div>
        The fundraiser file creation has failed too many times. Due to the use
        of significant server resources, this action can only be attempted three
        times within 24 hours. If there is an immediate need for this file
        download, please contact the admin with the fundraiser name and org for
        this restriction to be overridden.
      </div> //body
    ),
    [
      {
        label: "Okay",
        onClick: () => {
          hidePopup();
        },
      },
    ],
    "40vw" //width
  );

  const shipToHomeOrderErrorPopUp = new PopupModel(
    "Fundraiser Completion Error!", // header
    (
      <div>
        Ship to home orders have been found for this fundraiser and have not
        been marked as complete. A fundraiser cannot be marked as complete if
        there are still ship to home orders. Please complete them first!
      </div> //body
    ),
    [
      {
        label: "Okay",
        onClick: () => {
          hidePopup();
        },
      },
    ],
    "40vw" //width
  );

  const confirmCompleteFundraiserPopUp = new PopupModel(
    "Are you sure?",
    (
      <div>
        Marking this fundraiser as complete will set all bulk shipping and
        brochure orders from processing to complete and will remove them from
        view as well as the SKUS needed table.
      </div>
    ),
    [
      {
        label: "NO! Don't Complete Fundraiser",
        onClick: () => {
          hidePopup();
        },
      },
      {
        label: "Yes! I'm sure.",
        onClick: () => {
          markFundraiserAsComplete();
          hidePopup();
        },
      },
    ],
    "40vw"
  );

  const handleConfirmCompleteFundraiser = () => {
    showPopup(
      confirmCompleteFundraiserPopUp.header,
      confirmCompleteFundraiserPopUp.body,
      confirmCompleteFundraiserPopUp.buttons,
      confirmCompleteFundraiserPopUp.width
    );
  };

  return (
    <div className="warehouse-file-generation-container">
      <div className="button-container">
        {/* Generate Button + Status Text */}
        <div className="button-wrapper">
          <button
            onClick={handleFirstButtonClick}
            className="warehouse-file-generation-button generate"
            disabled={loading}
          >
            Generate Files
          </button>
          <div className="warehouse-file-generation-status">
            {currentGenerated ? (
              <p className="warehouse-file-generation-date">
                Files generated on: <span>{currentGenerated}</span>
              </p>
            ) : lastGenerated ? (
              <p className="warehouse-file-generation-date">
                Files last generated on:{" "}
                <span>{new Date(lastGenerated).toLocaleString()}</span>
              </p>
            ) : (
              <p className="warehouse-file-generation-message">
                Files have not been generated yet.
              </p>
            )}
          </div>
        </div>

        <div className="button-wrapper">
          <FundraiserBuyoutDownload
            fundraiserName={fundraiserName}
            participants={buyoutParticipants}
          />
        </div>
        <div className="button-wrapper">
          <button
            onClick={handleConfirmCompleteFundraiser}
            className="warehouse-file-generation-button generate"
          >
            Complete Fundraiser
          </button>
        </div>
      </div>

      {/* Loading Popup */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      <style>{`
      .warehouse-file-generation-container {
        font-family: Arial, sans-serif;
        text-align: center;
        margin: 20px;
        padding: 20px;
        border: 1px solid #ddd;
        border-radius: 8px;
        background-color: #f9f9f9;
      }

      .button-container {
        display: flex;
        justify-content: center;
        align-items: flex-start;
        gap: 15px;
      }

      .button-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
      }

      .warehouse-file-generation-button {
        font-size: 16px;
        padding: 10px 20px;
        cursor: pointer;
        border-radius: 4px;
        border: none;
        color: #fff;
        transition: background-color 0.3s;
      }

      .warehouse-file-generation-button.generate {
        background-color: #4caf50; /* Green */
      }

      .warehouse-file-generation-button.print {
        background-color: #008cba; /* Blue */
      }

      .warehouse-file-generation-button.disabled {
        background-color: #cccccc;
        cursor: not-allowed;
      }

      .warehouse-file-generation-button:hover:not(.disabled) {
        filter: brightness(90%);
      }

      .warehouse-file-generation-status {
        margin-top: 5px;
        font-size: 14px;
        color: #333;
      }

      .warehouse-file-generation-date {
        font-weight: bold;
      }

      .warehouse-file-generation-date span {
        color: #666;
      }

      .warehouse-file-generation-message {
        font-style: italic;
        color: #999;
      }

      /* Loading Spinner Styles */
      .loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 999;
        color: white;
      }

      .loading-spinner {
        border: 8px solid rgba(255, 255, 255, 0.3);
        border-top: 8px solid white;
        border-radius: 50%;
        width: 60px;
        height: 60px;
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
    </div>
  );
};

export default FileGenerationButtons;
